<script>
import appConfig from '@src/app.config.json'
import Layout from '@layouts/user-management.vue'
import UserMangementTable from '@components/tables/user-management-table.vue'
import OrgManagementTable from '@components/tables/org-management-table.vue'
import UserGroupManagementTable from '@components/tables/group-management-table.vue'
import { httpServices } from '@utils/http-services'

export default {
  page: {
    title: 'User Management',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    UserMangementTable,
    OrgManagementTable,
    UserGroupManagementTable,
  },
  data() {
    return {
      usersLoaded: false,
      orgsLoaded: false,
      groupsLoaded: false,
      users: [],
      orgs: [],
      groups: [],
      showOrg: this.$auth.user.profile.permissions.organization === 'vanished',
    }
  },
  mounted() {
    this.getUsers()
    this.getOrgs()
    this.getGroups()
  },
  methods: {
    getUsers() {
      httpServices.getUsers().then((res) => {
        this.users = res
        this.usersLoaded = true
      })
    },
    getGroups() {
      httpServices.getGroups().then((res) => {
        this.groups = res
        this.groupsLoaded = true
      })
    },
    getOrgs() {
      if (this.showOrg) {
        httpServices.getOrgs().then((res) => {
          this.orgs = res
          this.orgsLoaded = true
        })
      }
    },
    createGroup() {
      this.$router.push({
        name: 'create-group',
      })
    },
    createUser() {
      this.$router.push({
        name: 'create-user',
      })
    },
    createOrganization() {
      this.$router.push({
        name: 'create-organization',
      })
    },
    onDeleteUser(record) {
      this.usersLoaded = false
      if (record?.meta?.user_id) {
        httpServices.deleteAuth0User(record?.meta?.user_id).then(
          () => {
            httpServices
              .deleteUser(record.pk.replace('USER#', ''), record.organization)
              .then(
                () => {
                  this.$notification.success({
                    message: `User Deleted`,
                  })
                  this.getUsers()
                },
                () => {
                  this.usersLoaded = true
                  this.$notification.error({
                    message: `Failed to Delete User`,
                  })
                }
              )
          },
          () => {
            this.$notification.error({
              message: `Failed to Delete Auth User`,
            })
          }
        )
      } else {
        httpServices
          .deleteUser(record.pk.replace('USER#', ''), record.organization)
          .then(
            () => {
              this.$notification.success({
                message: `User Deleted`,
              })
              this.getUsers()
            },
            () => {
              this.usersLoaded = true
              this.$notification.error({
                message: `Failed to Delete User`,
              })
            }
          )
      }
    },
    onDeleteGroup(record) {
      this.groupsLoaded = false
      httpServices.deleteGroup(record.name, record.organization).then(
        (res) => {
          this.$notification.success({
            message: `Group Deleted`,
          })
          this.getGroups()
        },
        () => {
          this.$notification.error({
            message: `Failed to Delete`,
          })
        }
      )
    },
    onDeleteOrganization(record) {
      this.orgsLoaded = false
      httpServices.deleteOrganization(record.name).then(
        () => {
          this.$notification.success({
            message: `Organization Deleted`,
          })
          this.getOrgs()
        },
        () => {
          this.$notification.error({
            message: `Failed to Delete`,
          })
          this.orgsLoaded = true
        }
      )
    },
  },
}
</script>

<template>
  <Layout>
    <div id="um--container" class="ant-row">
      <div class="header">
        <h4>
          <a-icon type="user" style="padding-right: 8px" />
          Users
        </h4>
        <a-button type="ghost" icon="plus-circle" @click="createUser"
          >Add New
        </a-button>
      </div>
      <a-card style="width: 100%; margin-top: 0">
        <div v-if="!usersLoaded" class="loader--container">
          <a-icon
            type="loading"
            style="padding-left: 8px; font-size: 24px; color: #1890ff"
            spin
          />
        </div>
        <div v-else>
          <UserMangementTable :users="users" @deleteUser="onDeleteUser">
          </UserMangementTable>
        </div>
      </a-card>

      <div class="header">
        <h4>
          <a-icon type="skin" style="padding-right: 8px" />
          User Groups
        </h4>
        <a-button type="ghost" icon="plus-circle" @click="createGroup"
          >Add New
        </a-button>
      </div>
      <a-card style="width: 100%; margin-top: 0">
        <div v-if="!groupsLoaded" class="loader--container">
          <a-icon
            type="loading"
            style="padding-left: 8px; font-size: 24px; color: #1890ff"
            spin
          />
        </div>
        <div v-else>
          <UserGroupManagementTable
            v-if="groupsLoaded"
            :groups="groups"
            @deleteGroup="onDeleteGroup"
          ></UserGroupManagementTable>
        </div>
      </a-card>

      <div v-if="showOrg">
        <div class="header">
          <h4>
            <a-icon type="team" style="padding-right: 8px" />
            Organizations
          </h4>
          <a-button type="ghost" icon="plus-circle" @click="createOrganization"
            >Add New
          </a-button>
        </div>
        <a-card style="width: 100%; margin-top: 0">
          <div v-if="!orgsLoaded" class="loader--container">
            <a-icon
              type="loading"
              style="padding-left: 8px; font-size: 24px; color: #1890ff"
              spin
            />
          </div>
          <div v-else>
            <OrgManagementTable
              v-if="showOrg"
              :orgs="orgs"
              @deleteOrganization="onDeleteOrganization"
            ></OrgManagementTable>
          </div>
        </a-card>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss">
@import '@design';

#um--container {
  position: relative;
  width: 100%;
  padding: 16px;
  margin: 0 0 16px;
  background: white;
  border: 1px solid #ebedf0;
  box-shadow: 4px 8px 24px 5px rgb(208 216 243 / 60%);
  transition: all 0.2s;

  .ant-card {
    margin: 1.6rem auto;

    .ant-card-head-title {
      font-weight: 700;
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  table tr td {
    text-transform: capitalize;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.2rem;
    margin-bottom: 0;
    text-align: left;
    background: #141414;
    border-radius: 4px 4px 0 0;

    h4 {
      margin: 0;
      font-weight: 100;
      color: white;
    }
  }
}
</style>
